<template>
  <div class="two-factor-profile border rounded p-3 mb-3">
    <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
      <div>
        <h5 class="h6 mb-1">
          <i class="fas fa-shield-alt me-1" aria-hidden="true"></i>
          {{ $t("twoFactor.title") }}
        </h5>
        <p class="small mb-0" :class="enabled ? 'text-success' : 'text-muted'">
          {{ enabled ? $t("twoFactor.enabled") : $t("twoFactor.disabled") }}
        </p>
      </div>
      <div class="d-flex flex-wrap gap-2" v-if="!loading">
        <button
          v-if="!enabled && !setup"
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="busy"
          @click="startSetup"
        >
          <i v-if="busy" class="fas fa-spinner fa-spin me-1" aria-hidden="true"></i>
          {{ $t("twoFactor.activate") }}
        </button>
        <button
          v-if="enabled && !disabling"
          type="button"
          class="btn btn-outline-secondary btn-sm"
          @click="disabling = true; code = ''; error = ''"
        >
          {{ $t("twoFactor.deactivate") }}
        </button>
      </div>
    </div>

    <div v-if="setup" class="mt-3">
      <ol class="small ps-3 mb-2">
        <li>{{ $t("twoFactor.stepInstall") }}</li>
        <li>{{ $t("twoFactor.stepScan") }}</li>
        <li>{{ $t("twoFactor.stepConfirm") }}</li>
      </ol>
      <div class="d-flex flex-wrap align-items-center gap-3">
        <div class="two-factor-qr bg-white p-2 rounded">
          <qrcode-vue :value="setup.otpauthUrl" :size="200" />
        </div>
        <div class="small">
          <div class="text-muted">{{ $t("twoFactor.manualKey") }}</div>
          <code class="two-factor-secret">{{ groupedSecret }}</code>
        </div>
      </div>
      <div class="d-flex flex-wrap gap-2 mt-3">
        <input
          v-model="code"
          class="form-control form-control-sm two-factor-code"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="6"
          :placeholder="$t('twoFactor.codePlaceholder')"
          @keydown.enter.prevent="code && !busy && confirmSetup()"
        />
        <button type="button" class="btn btn-primary btn-sm" :disabled="busy || !code" @click="confirmSetup">
          {{ $t("twoFactor.confirm") }}
        </button>
        <button type="button" class="btn btn-link btn-sm" :disabled="busy" @click="setup = null">
          {{ $t("twoFactor.cancel") }}
        </button>
      </div>
    </div>

    <div v-if="disabling" class="mt-3">
      <p class="small mb-2">{{ $t("twoFactor.disableHint") }}</p>
      <div class="d-flex flex-wrap gap-2">
        <input
          v-model="code"
          class="form-control form-control-sm two-factor-code"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="6"
          :placeholder="$t('twoFactor.codePlaceholder')"
          @keydown.enter.prevent="code && !busy && confirmDisable()"
        />
        <button type="button" class="btn btn-danger btn-sm" :disabled="busy || !code" @click="confirmDisable">
          {{ $t("twoFactor.deactivate") }}
        </button>
        <button type="button" class="btn btn-link btn-sm" :disabled="busy" @click="disabling = false">
          {{ $t("twoFactor.cancel") }}
        </button>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger small py-2 mt-2 mb-0">{{ error }}</div>
  </div>
</template>

<script>
import QrcodeVue from "qrcode-vue";
import Swal from "sweetalert2";
import { wencesApi } from "@/api/wencesApi";

export default {
  name: "TwoFactorProfileSetup",
  components: { QrcodeVue },
  data() {
    return {
      loading: true,
      enabled: false,
      setup: null,
      disabling: false,
      code: "",
      busy: false,
      error: "",
    };
  },
  computed: {
    groupedSecret() {
      return (this.setup?.secret || "").replace(/(.{4})/g, "$1 ").trim();
    },
  },
  async created() {
    try {
      const { data } = await wencesApi.get("/api/users/2fa/status");
      this.enabled = data?.data?.enabled === true;
    } catch (_) {
      this.enabled = false;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    apiError(e) {
      return e?.response?.data?.error || this.$t("twoFactor.genericError");
    },
    async startSetup() {
      this.busy = true;
      this.error = "";
      try {
        const { data } = await wencesApi.post("/api/users/2fa/setup");
        this.setup = data.data;
        this.code = "";
      } catch (e) {
        this.error = this.apiError(e);
      } finally {
        this.busy = false;
      }
    },
    async confirmSetup() {
      this.busy = true;
      this.error = "";
      try {
        await wencesApi.post("/api/users/2fa/enable", { code: this.code.trim() });
        this.enabled = true;
        this.setup = null;
        this.code = "";
        Swal.fire({ title: this.$tn("twoFactor.enabledOk"), icon: "success", position: "top" });
      } catch (e) {
        this.error = this.apiError(e);
      } finally {
        this.busy = false;
      }
    },
    async confirmDisable() {
      this.busy = true;
      this.error = "";
      try {
        await wencesApi.post("/api/users/2fa/disable", { code: this.code.trim() });
        this.enabled = false;
        this.disabling = false;
        this.code = "";
      } catch (e) {
        this.error = this.apiError(e);
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.two-factor-code {
  max-width: 140px;
  letter-spacing: 0.2em;
}
.two-factor-secret {
  font-size: 0.95rem;
  word-break: break-all;
}
.two-factor-qr {
  line-height: 0;
}
</style>
